import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Tooltip, theme } from 'antd';
import {
  AppstoreOutlined,
  BellOutlined,
  DashboardOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MessageOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { ROUTES } from '@shared/config/routes';
import { useLogout } from '@features/auth';
import { useCurrentUserQuery } from '@entities/session';
import { useUnreadNotificationsCount } from '@entities/notification';
import { ThemeToggle } from '@widgets/theme-toggle';
import { KycBanner } from '@widgets/kyc-banner';
import { GlobalInbox } from '@widgets/global-inbox';

const { Sider, Header, Content } = Layout;

type NavItem = {
  key: string;
  labelKey: string;
  icon: React.ReactNode;
};

const NAV: NavItem[] = [
  { key: ROUTES.root, labelKey: 'nav.dashboard', icon: <DashboardOutlined /> },
  { key: ROUTES.profile, labelKey: 'nav.profile', icon: <IdcardOutlined /> },
  { key: ROUTES.products, labelKey: 'nav.catalog', icon: <AppstoreOutlined /> },
  { key: ROUTES.buyRequests, labelKey: 'nav.buyRequests', icon: <ShopOutlined /> },
  { key: ROUTES.quotes, labelKey: 'nav.myQuotes', icon: <UnorderedListOutlined /> },
  { key: ROUTES.orders, labelKey: 'nav.orders', icon: <ShoppingCartOutlined /> },
  { key: ROUTES.chats, labelKey: 'nav.chats', icon: <MessageOutlined /> },
  { key: ROUTES.notifications, labelKey: 'nav.notifications', icon: <BellOutlined /> },
  { key: ROUTES.settings, labelKey: 'nav.settings', icon: <SettingOutlined /> },
];

function pickActiveKey(pathname: string): string {
  const direct = NAV.find((n) => n.key === pathname);
  if (direct) return direct.key;
  const prefixMatches = NAV.filter(
    (n) => n.key !== '/' && pathname.startsWith(n.key + '/'),
  ).sort((a, b) => b.key.length - a.key.length);
  return prefixMatches[0]?.key ?? '/';
}

export function AppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { token } = theme.useToken();
  const { data: user } = useCurrentUserQuery();
  const { data: unread } = useUnreadNotificationsCount();
  const activeKey = useMemo(() => pickActiveKey(location.pathname), [location.pathname]);

  const userMenu = {
    items: [
      { key: 'settings', icon: <SettingOutlined />, label: t('common.settings') },
      { type: 'divider' as const },
      { key: 'logout', icon: <LogoutOutlined />, label: t('common.logout'), danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'settings') navigate(ROUTES.settings);
      if (key === 'logout') void logout();
    },
  };

  const userInitial = (user?.email ?? 'AC').slice(0, 2).toUpperCase();

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <GlobalInbox />
      <Sider
        width={240}
        breakpoint="lg"
        collapsedWidth={64}
        style={{ background: token.colorBgContainer, borderRight: `1px solid ${token.colorBorderSecondary}` }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 64,
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: 1,
            color: token.colorTextHeading,
          }}
        >
          AIDU
        </div>
        <Menu
          mode="inline"
          selectedKeys={[activeKey]}
          onClick={({ key }) => navigate(key)}
          items={NAV.map((n) => ({ key: n.key, icon: n.icon, label: t(n.labelKey) }))}
          style={{ borderInlineEnd: 'none', paddingInline: 8 }}
        />
        <div style={{ padding: 12 }}>
          <KycBanner />
        </div>
      </Sider>
      <Layout>
        <Header
          style={{
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            paddingInline: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
          }}
        >
          <Space size={12}>
            <ThemeToggle />
            <Tooltip title={t('nav.notifications')}>
              <Button
                type="text"
                onClick={() => navigate(ROUTES.notifications)}
                icon={
                  <BellOutlined
                    style={{
                      color: unread?.count ? token.colorError : token.colorTextSecondary,
                    }}
                  />
                }
              >
                {unread?.count ? unread.count : null}
              </Button>
            </Tooltip>
            <Dropdown menu={userMenu} placement="bottomRight" arrow>
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size={32}
                  style={{ backgroundColor: token.colorPrimary, fontWeight: 600 }}
                >
                  {userInitial}
                </Avatar>
                <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                  {user?.email ?? '—'}
                </span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ padding: 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
